<template>
  <div>
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <h2 class="has-text-danger has-text-centered">
      <b>{{ $t("credits.attention") }}:</b>
      {{ $t("credits.attentionMessage1") }} <u>{{ $t("credits.added") }}</u>
      {{ $t("credits.attentionMessage2") }}
    </h2>
    <br />

    <div class="columns">
      <div class="column">
        <b-field :label="$t('common.user')">
          <b-field>
            <b-select placeholder="ID" v-model="id">
              <option value="personalId">{{ $t("common.personalId") }}</option>
              <option value="cellphone">{{ $t("common.cellphone") }}</option>
              <option value="email">{{ $t("common.email") }}</option>
            </b-select>
            <b-input
              v-if="id === 'personalId'"
              v-model="personalId"
              placeholder="123.456.789-10"
              @input.native="personalId = formatPersonalId('personalId')"
              @keyup.enter.native="findUser"
            ></b-input>
            <b-input
              v-if="id === 'cellphone'"
              v-model="cellphone"
              placeholder="(11) 99999-9999"
              @input.native="formatCellphone('cellphone')"
              @keyup.enter.native="findUser"
            ></b-input>
            <b-input
              v-if="id === 'email'"
              v-model="email"
              placeholder="email@do.usuario"
              @keyup.enter.native="findUser"
            ></b-input>
            <p class="control">
              <button class="button is-info" @click="findUser">
                {{ $t("credits.search") }}
              </button>
            </p>
          </b-field>
        </b-field>

        <div v-if="user._id">
          <p>
            <b>{{ $t("common.id") }}:</b>
            {{ user._id }}
          </p>

          <p
            v-if="
              permissions.seeStoreClientName ||
                permissions.seeStoreClientSurname
            "
          >
            {{ `${user.personalInfo.name} ${user.personalInfo.surname}` }}
          </p>

          <p v-if="permissions.seeStoreClientPersonalId">
            {{ user.personalInfo.personalId }}
          </p>

          <p v-if="permissions.seeStoreClientCellphone">
            {{
              `${user.personalInfo.countryCallingCode} ${user.personalInfo.cellphone}`
            }}
          </p>

          <p v-if="permissions.seeStoreClientEmail">
            {{ user.personalInfo.email }}
          </p>
        </div>
      </div>

      <div class="column">
        <b-field :label="$t('common.store')">
          <b-select
            :placeholder="$t('credits.pickStore')"
            v-model="storeId"
            expanded
          >
            <option value="">{{ $t("credits.pickStore") }}</option>
            <option v-for="s in stores" :key="s._id" :value="s._id">
              {{ s.storeInfo.name }}
            </option>
          </b-select>
        </b-field>

        <b-field
          :label="
            `${$t('credits.valueBetween')} ${payments.currencySymbol}1 - ${
              payments.currencySymbol
            }200`
          "
        >
          <money
            v-model="value"
            v-bind="money"
            class="input is-expanded"
          ></money>
        </b-field>

        <p>
          {{
            `${$t("credits.royaltyFranchiser", {
              defaultRoyaltyPercentage: royaltyPercentage
            })}:
            ${payments.currencySymbol} ${(
              value *
              (royaltyPercentage / 100)
            ).toFixed(2)}`
          }}
        </p>
      </div>
    </div>

    <hr />

    <div class="columns">
      <div class="column">
        <p>
          <b>{{ $t("credits.cashbackCededBy") }}:</b>
          <br />
          {{
            `${ownerInfo.name} (${$t("common.licensee")}) - ${ownerInfo.email}`
          }}
        </p>
        <br />

        <b-field :label="$t('credits.creditReason')">
          <b-input
            v-model="grantedBy.grantReason"
            :placeholder="$t('credits.creditReasonDescription')"
          ></b-input>
        </b-field>
      </div>
    </div>
    <br />

    <div class="has-text-centered">
      <button
        class="button is-medium"
        :style="{
          backgroundColor: $primaryColor,
          color: $primaryColorContrast
        }"
        @click="giveCredit"
      >
        {{ $t("credits.cede") }}
      </button>
    </div>
  </div>
</template>

<script>
import { common } from "../../mixins/common";
import { mask } from "../../mixins/mask";
import { Money } from "v-money";
import { mapGetters } from "vuex";

export default {
  props: ["stores"],

  mixins: [common, mask],

  components: {
    Money
  },

  data() {
    return {
      isLoading: false,
      id: "personalId",
      personalId: "",
      cellphone: "",
      email: "",
      money: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        precision: 2
      },

      user: "",
      storeId: "",
      value: 0,
      grantedBy: {
        grantReason: ""
      }
    };
  },

  methods: {
    findUser() {
      if (
        this.id === "personalId" &&
        (!this.personalId || this.personalId.length !== 14)
      ) {
        this.alertError({ message: "INVALID_USER_ID" });
        return;
      } else if (this.id === "email" && !this.email) {
        this.alertError({ message: "INVALID_EMAIL" });
        return;
      }

      let cellphone = "";
      if (this.id === "cellphone") {
        if (
          !this.cellphone ||
          (this.cellphone.length !== 14 && this.cellphone.length !== 15)
        ) {
          this.alertError({ message: "INVALID_CELLPHONE" });
          return;
        }
        cellphone = this.cellphone.replace(/\D/g, "");
        if (cellphone.length !== 10 && cellphone.length !== 11) {
          this.alertError({ message: "INVALID_CELLPHONE" });
          return;
        }
        cellphone = `${cellphone.substring(0, 7)}-${cellphone.substring(7)}`;
      }

      this.isLoading = true;
      this.$store
        .dispatch("findUser", {
          id: this.id,
          value:
            this.id === "personalId"
              ? this.personalId
              : this.id === "cellphone"
              ? cellphone
              : this.id === "email"
              ? this.email
              : ""
        })
        .then(res => {
          if (res.data.result === "error") {
            this.user = "";
            this.alertError(res.data);
          } else {
            this.user = res.data.payload.user;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    giveCredit() {
      if (!this.user._id) {
        this.alertError({ message: "INVALID_USER_ID" });
        return;
      } else if (!this.storeId) {
        this.alertError({ message: "INVALID_STORE" });
        return;
      } else if (!this.value) {
        this.alertError({ message: "INVALID_VALUE" });
        return;
      } else if (!this.grantedBy.grantReason) {
        this.alertError({ message: "INVALID_GRANT_REASON" });
        return;
      }
      this.isLoading = true;
      this.$store
        .dispatch("giveCredit", {
          userId: this.user._id,
          storeId: this.storeId,
          value: Number(String(this.value.toFixed(2)).replace(/[^0-9]+/g, "")),
          grantedBy: {
            grantReason: this.grantedBy.grantReason
          }
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$emit("get-credits");
            this.$parent.$parent.close();
            this.$buefy.toast.open({
              message: this.$t("credits.cashbackCeded"),
              type: "is-success",
              position: "is-bottom",
              duration: 5000
            });
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  },

  watch: {
    "payments.currencySymbol"(newVal) {
      this.money.prefix = `${newVal} `;
    }
  },

  computed: {
    ...mapGetters(["ownerInfo", "payments", "permissions"]),
    royaltyPercentage() {
      let store = this.stores.find(e => e._id == this.storeId);
      return store
        ? store.storeInfo.royaltyPercentage
        : this.payments.defaultRoyaltyPercentage;
    }
  },

  mounted() {
    this.money.prefix = `${this.payments.currencySymbol} `;
  }
};
</script>
